import { skipToken } from '@tanstack/react-query'
import { useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  type SubmissionAddress,
  submissionAddressKey,
} from '@/lib/audition/submission-address'
import { trpc } from '@/lib/trpc'
import { EnrollmentCta } from './enrollment-cta'
import { LeetcodeStep } from './leetcode-step'
import { PickTime } from './pick-time'

/**
 * The post-submission "boost your chances" hub (LG-1031).
 *
 * One component, two call sites — the just-submitted done screen and the
 * returning one. The surfaces used to be inlined in the wizard behind
 * `token &&`, so the signed-in-without-a-token path rendered an empty done
 * screen with no error and no explanation.
 *
 * Takes a submission address and nothing else, matching its own children
 * (`PickTime`, `LeetcodeStep`): a caller supplies the address — the emailed
 * resume token, or the row id under the caller's own session (LG-1056) —
 * and the surface fetches what it needs. That is also what keeps the hub
 * honest — the stage and artifact refs it renders are read back by the SAME
 * address its mutations write through, so the row on screen is the row that
 * gets written, by construction rather than by two queries agreeing.
 *
 * Flat, not nested: each surface gates on its OWN condition and renders
 * itself away when it does not apply, so an applicant mid-flow sees only
 * what is actually open to them.
 */
export function BoostHub({ address }: { address: SubmissionAddress | null }) {
  return (
    <section
      aria-labelledby="boost-hub-title"
      data-testid="boost-hub"
      className="space-y-4"
    >
      <div>
        <h2 id="boost-hub-title" className="text-lg font-semibold">
          Boost your chances
        </h2>
        <p className="text-sm text-muted-foreground">
          All optional. Your application is already in — these just give us more
          to go on.
        </p>
      </div>

      {/* The header and the CV notice above/below never depend on the address,
          so the hub is never a blank space while the address resolves or
          after it fails. Only the surfaces that actually mutate wait. */}
      {/* Keyed by the address, so a change of address is a change of surface.
          `artifact` below is per-submission state — "I just saved/skipped" —
          and the wizard can re-point this hub at a different row while it
          stays mounted (start a new application in a tab that never
          reloads). Carried across, a stale 'saved' acknowledges a link the
          new row does not have and a stale 'skipped' hides the ask entirely. */}
      <BoostSurfaces
        key={address ? submissionAddressKey(address) : 'no-address'}
        address={address}
      />

      <CvPlaceholder />

      {/* The Loom intro slot is LG-424 and lands here, below the CV. */}
    </section>
  )
}

/**
 * The addressed surfaces, and the three states of resolving the address.
 *
 * Separate from the shell above so a failure here costs the applicant these
 * panels and not the whole hub — the silent-empty-screen failure mode is the
 * one LG-1031 exists to remove, and re-creating it one layer down would be
 * the same bug with a better excuse.
 */
function BoostSurfaces({ address }: { address: SubmissionAddress | null }) {
  // Local, not lifted: "I just skipped/saved the artifact" is a fact about
  // this render of this screen, and it dies with it. The durable answer is
  // `leetcodeUrl` coming back from the server on the next load.
  const [artifact, setArtifact] = useState<'open' | 'saved' | 'skipped'>('open')

  // `skipToken`, not `{ token: '' }` + `enabled: false`: an empty token is an
  // in-band sentinel that type-checks as a `SubmissionAddress` but can never
  // pass `submissionAddressSchema` (`min(1)`), so the type would be asserting
  // a validity the value does not have — with only `enabled` keeping the
  // server from seeing it, and `refetch()` below ignores `enabled` in React
  // Query v5. Out of band, no address means there is no request to make.
  const submission = trpc.audition.get.useQuery(address ?? skipToken)

  // No address yet, or the query is still in flight. The shell above is
  // already on screen, so this is a gap in a populated page, not a blank one.
  if (!address || submission.isPending) return null

  // UNAUTHORIZED is terminal from here: the token expired, or the address is
  // a row id and the request carried no session (LG-1056). A retry re-sends
  // the same credential and gets the same refusal, so — as AccountStep does
  // for CONFLICT — say what would work instead and offer no button.
  if (submission.isError && submission.error.data?.code === 'UNAUTHORIZED') {
    return (
      <p role="status" className="text-sm text-muted-foreground">
        Sign in to see these, or open the link from your email.
      </p>
    )
  }

  if (submission.isError) {
    return (
      // `role="status"` because this whole block IS the feedback: the retry
      // swaps one line of static prose for another, and without a live
      // region a screen-reader applicant presses the button and hears
      // nothing — not the attempt, not the outcome.
      <p role="status" className="text-sm text-muted-foreground">
        We couldn't load your extras just now.{' '}
        {/* React Query holds `status === 'error'` for the whole refetch, so
            without a pending state the click changes nothing on screen and an
            applicant on a slow connection just stacks retries.

            `aria-disabled`, not `disabled`: a truly disabled button is
            unfocusable, so the browser blurs the element the applicant just
            activated and drops them to the top of the document. The handler
            does the actual guarding. */}
        <Button
          variant="link"
          className="h-auto p-0 text-sm"
          aria-disabled={submission.isFetching}
          onClick={() => {
            if (!submission.isFetching) submission.refetch()
          }}
        >
          {submission.isFetching ? 'Trying…' : 'Try again'}
        </Button>
      </p>
    )
  }

  const { stage, leetcodeUrl, leetcodeNotes } = submission.data

  return (
    <>
      {/* Landing spot for the CIO unlock email. Renders itself away unless
          the stage says payment is open. */}
      <EnrollmentCta address={address} stage={stage} />

      {/* Renders itself away when there is no open proposal. */}
      <PickTime address={address} />

      {/* Second chance at the optional artifact: Background asks for it and
          takes a blank, so this is where "you can add it later" actually
          becomes true. */}
      {artifact === 'saved' ? (
        // The slot stays occupied. Unmounting the step on save would look
        // exactly like the skip path, so an applicant who pasted a link and
        // pressed save would have no way to tell it landed.
        <p className="text-sm text-muted-foreground">
          Saved — thanks, we'll take a look.
        </p>
      ) : (
        artifact === 'open' &&
        !leetcodeUrl && (
          <LeetcodeStep
            address={address}
            // A panel under the hub's h2, not a screen of its own.
            headingLevel={3}
            initialUrl={null}
            // Notes save independently of the link, so this instance must
            // hydrate them: the save writes notes wholesale, and a null here
            // would silently wipe notes saved earlier without a link.
            initialNotes={leetcodeNotes}
            onSkip={() => setArtifact('skipped')}
            onSaved={() => setArtifact('saved')}
          />
        )
      )}
    </>
  )
}

/**
 * The CV ask, deliberately not built (LG-918 makes it real).
 *
 * There is no file input in this markup at all — not a disabled one. A
 * control that opens a picker and then drops the file is worse than no
 * control: the applicant believes they have sent us a CV. So this renders
 * as an announcement of a thing that is coming, and there is nothing here
 * to click, drag onto, or submit.
 */
function CvPlaceholder() {
  return (
    <div
      data-testid="boost-cv-placeholder"
      className="rounded-md border border-dashed p-4"
    >
      <div className="flex items-center gap-2">
        {/* h3, under the hub's h2: the sibling surfaces render their own
            headings at this level too, so the outline matches the nesting. */}
        <h3 className="text-base font-semibold text-muted-foreground">
          Send us your CV
        </h3>
        <Badge variant="secondary">Coming soon</Badge>
      </div>
      <p className="mt-1 text-sm text-muted-foreground">
        We're building this right now. Nothing to do yet — when it's ready
        you'll be able to attach your CV here.
      </p>
    </div>
  )
}
